import { z } from "zod";
// Per-customer Analytics for staff (ADR 0017, `docs/adr/0017-per-customer-analytics-access-and-read-back.md`): the contract that every
// analytics lane shares. CHT-2027 owns this file. The routes are in `apps/api/src/routes/staff-analytics/`; the admin calls them only through
// `@lafayette/api-client` (`staff-analytics.ts`).
//
// STAFF ONLY and closed: strict shapes, ids, counts, money in minor units and closed codes. No message text, no note, no event property.
// A number that cannot be read is absent, never a made-up zero.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const isoDateTime = z.iso.datetime({ offset: false });
const count = z.number().int().min(0).max(1_000_000_000);
const minor = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const seconds = z.number().int().min(0).max(1_000_000_000);
export const ANALYTICS_RANGES = ["24h", "7d", "30d", "90d", "12m", "all"];
export const ANALYTICS_INTERVALS = ["hour", "day", "week"];
export const ANALYTICS_COMPARE = ["none", "previous_period"];
export const ANALYTICS_TABS = ["overview", "explore", "events"];
export const APP_DATA_AVAILABILITY = [
    "AVAILABLE",
    "OPTED_OUT",
    "ANALYTICS_OFF",
    "VENDOR_UNAVAILABLE",
];
export const ANALYTICS_ACCESS_REASONS = [
    "SUPERVISOR",
    "ADMINISTRATOR",
    "PRIMARY",
    "BACKUP",
    "TEMPORARY",
];
/** The closed list of the screens of the phone app (the domain list of `ANALYTICS_SCREEN_NAMES`). */
export const ANALYTICS_SCREEN_KEYS = [
    "concierge",
    "trips",
    "trip_detail",
    "trip_new",
    "trip_edit",
    "itinerary_add",
    "itinerary_review",
    "itinerary_edit",
    "actions",
    "action_detail",
    "saved",
    "saved_item",
    "profile",
    "memory",
    "request_new",
    "request_answer",
    "sign_in",
    "other",
];
export const ANALYTICS_OPENING_HEADER = "X-Analytics-Opening";
/** The time zone the admin app sends when the browser gives none (open point Q9). */
export const ANALYTICS_DEFAULT_TIME_ZONE = "Asia/Kolkata";
/** An IANA zone name, as the browser gives it (`Asia/Kolkata`, `Europe/Lisbon`). The server checks it against the database list. */
const ianaZone = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_+\-/]*$/u);
/** The id of an audited opening (decision 10). Every read sends it in `X-Analytics-Opening`. */
export const analyticsOpeningIdSchema = uuid;
export const staffAnalyticsWindowQuerySchema = z
    .strictObject({
    range: z.enum(ANALYTICS_RANGES).default("30d"),
    // Both or neither: the drag-to-zoom window of Explore (CHT-2029).
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    tz: ianaZone.default(ANALYTICS_DEFAULT_TIME_ZONE),
})
    .refine((value) => (value.from === undefined) === (value.to === undefined));
/** `GET .../overview`: the range and the zone only (the Overview has no zoom window). */
export const staffAnalyticsOverviewQuerySchema = z.strictObject({
    range: z.enum(ANALYTICS_RANGES).default("30d"),
    tz: ianaZone.default(ANALYTICS_DEFAULT_TIME_ZONE),
});
export const staffAnalyticsAccessSchema = z.strictObject({
    customerId: uuid,
    displayName: z.string().max(120),
    memberSince: isoDateTime,
    accessReason: z.enum(ANALYTICS_ACCESS_REASONS),
    consent: z.strictObject({ optedOut: z.boolean(), readAt: isoDateTime }),
    retentionMonths: z.literal(13),
    window: z.strictObject({
        from: isoDateTime,
        to: isoDateTime,
        tz: z.string().max(64),
        appDataFrom: isoDateTime,
    }),
});
// --- the picker ---------------------------------------------------------------------------------------------------------------------
const pageCursor = z
    .string()
    .min(1)
    .max(512)
    .regex(/^[A-Za-z0-9_-]+$/u);
export const staffAnalyticsCustomersQuerySchema = z.strictObject({
    query: z.string().min(1).max(64).optional(),
    cursor: pageCursor.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const staffAnalyticsCustomerPageSchema = z.strictObject({
    rows: z
        .array(z.strictObject({
        customerId: uuid,
        displayName: z.string().max(120),
        accessReason: z.enum(ANALYTICS_ACCESS_REASONS),
    }))
        .max(50),
    nextCursor: pageCursor.nullable(),
});
// --- the opening --------------------------------------------------------------------------------------------------------------------
export const staffAnalyticsOpeningBodySchema = z.strictObject({ tab: z.enum(ANALYTICS_TABS) });
export const staffAnalyticsOpeningSchema = z.strictObject({
    openingId: analyticsOpeningIdSchema,
    access: staffAnalyticsAccessSchema,
});
// --- the Overview, from records only ------------------------------------------------------------------------------------------------
export const ANALYTICS_REQUEST_TYPES = [
    "DINING",
    "VILLA",
    "EVENT",
    "EXPERIENCE",
    "SERVICE",
    "TRANSPORT",
    "OTHER",
];
export const staffAnalyticsOverviewSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    records: z.strictObject({
        currency: z.literal("INR"),
        kpis: z.strictObject({
            /** Paid and not refunded, summed over every payment up to the end of the window. */
            lifetimeValueMinor: minor,
            /** The same sum up to the START of the window: the change in the window is the difference. */
            lifetimeValuePreviousMinor: minor,
            /** Counts of what was created (or sent) inside the window. `savedItems` counts the saves that were not undone. */
            trips: count,
            requests: count,
            quoted: count,
            paid: count,
            savedItems: count,
            conversations: count,
            handoffs: count,
            notificationsSent: count,
        }),
        /**
         * The requests submitted in the window, and how many of them reached each stage since. A stage can be above the one before it: a free request is
         * confirmed with no quote and no payment.
         */
        requestFunnel: z.strictObject({
            submitted: count,
            quoted: count,
            paid: count,
            confirmed: count,
            completed: count,
        }),
        /** The median time from submit to the first approved quote, over the requests that have one. `null`: no quote in the window. */
        medianSecondsToQuote: seconds.nullable(),
        requestsByType: z
            .array(z.strictObject({ type: z.enum(ANALYTICS_REQUEST_TYPES), count: count.min(1) }))
            .max(ANALYTICS_REQUEST_TYPES.length),
        /** One entry per calendar month (in `tz`) that has a payment, oldest first. */
        spendByMonth: z
            .array(z.strictObject({
            month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u),
            amountMinor: minor,
            payments: count.min(1),
        }))
            .max(120),
        /** Records per bucket of the interval, oldest first, with no gap: a bucket with nothing is zeros. */
        activity: z.strictObject({
            interval: z.enum(ANALYTICS_INTERVALS),
            points: z
                .array(z.strictObject({
                t: isoDateTime,
                customerMessages: count,
                requests: count,
                payments: count,
                trips: count,
            }))
                .max(1000),
        }),
        messages: z.strictObject({ customer: count, assistant: count, concierge: count }),
        service: z.strictObject({
            /** Assistant replies (completed or degraded) to the customer's messages of the window. */
            assistantReplies: count,
            /** The median from the customer's message to the end of the assistant's reply. `null`: no reply. */
            assistantFirstReplyMedianSeconds: seconds.nullable(),
            /** The median from a handoff request to a person accepting it. `null`: none accepted. */
            waitForPersonMedianSeconds: seconds.nullable(),
        }),
    }),
    appData: z.strictObject({ availability: z.enum(APP_DATA_AVAILABILITY) }),
    /**
     * The app summary of the window, from the derived visit read model (CHT-2029): sessions, time in the app and where the time went by screen (the
     * five biggest and the rest in `other`). ABSENT unless `appData.availability` is `AVAILABLE`: a customer who opted out, or analytics off, has records only.
     */
    app: z
        .strictObject({
        sessions: count,
        timeInAppSeconds: seconds,
        whereTimeWent: z
            .array(z.strictObject({ screen: z.enum(ANALYTICS_SCREEN_KEYS), seconds }))
            .max(6),
    })
        .optional(),
});
